'use client';

import React from 'react';
import { ChatTeardropDotsIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { useAskEllieSidebarStore } from '@/state/askEllieSidebarState';

interface AskEllieButtonProps {
  /** Widget name handed to the sidebar, and the accessible name of the button. */
  widgetTitle: string;
  /** Serialized widget data the sidebar sends along as conversation context. */
  widgetContext: string;
  /** Visible chip text, for suggested-prompt chips where the prompt itself is
   * the label. Defaults to "Ask Ellie"; when set it also becomes the
   * accessible name, so a row of prompt chips never reads as N identical
   * "Ask Ellie" buttons. */
  label?: string;
  className?: string;
}

/**
 * The "Ask Ellie" trigger on its own, for cards that host it inline (e.g. in an
 * `InsightCard` `headerRight` slot) rather than letting `AskEllieWrapper`
 * position it.
 *
 * Always visible by design: it used to be revealed on card hover, which meant
 * users never discovered it unless they happened to mouse over the right card.
 * It reads as a quiet secondary action at rest and picks up the brand tint on
 * hover/focus, so it stays discoverable without competing with the card's data.
 *
 * Clicking loads this widget's context into the shared Ask Ellie sidebar and
 * opens it — it does not navigate.
 *
 * Args:
 *     widgetTitle: Widget name shown in the sidebar; also the accessible name.
 *     widgetContext: Serialized widget data sent as conversation context.
 *     className: Extra classes for the button (positioning/spacing only).
 *
 * Example:
 *     <InsightCard
 *       title="Spend Trend"
 *       headerRight={
 *         <AskEllieButton
 *           widgetTitle="Spend Trend"
 *           widgetContext={ctx}
 *           className="ml-auto"
 *         />
 *       }
 *     />
 */
/**
 * Open the Ellie sidebar with a widget's context.
 *
 * Extracted so the pill below and the row-shaped triggers in
 * `memberBrief.parts.tsx` share one path into the store. They are two shapes
 * for two densities, not two behaviours, and a `variant` prop on the pill
 * would have made one component answer for both.
 *
 * The returned handler stops propagation: cards are often clickable (row
 * drill-ins, modals), and asking Ellie must never double as navigation.
 */
export function useAskEllie() {
  const openSidebar = useAskEllieSidebarStore((s) => s.openSidebar);
  const setWidgetContext = useAskEllieSidebarStore((s) => s.setWidgetContext);
  return React.useCallback(
    (widgetTitle: string, widgetContext: string) => (e: React.MouseEvent) => {
      e.stopPropagation();
      setWidgetContext(widgetTitle, widgetContext);
      openSidebar();
    },
    [openSidebar, setWidgetContext]
  );
}

export function AskEllieButton({
  widgetTitle,
  widgetContext,
  label,
  className,
}: AskEllieButtonProps) {
  const handleClick = useAskEllie()(widgetTitle, widgetContext);

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={label ?? `Ask Ellie about ${widgetTitle}`}
      title={label}
      className={cn(
        'motion-tap inline-flex shrink-0 items-center gap-1.5 rounded-full',
        'border border-ds-border-subtlest bg-ds-surface-white px-2.5 py-1',
        'ds-text-body-xs-medium text-ds-text-subtle',
        'transition-colors duration-150 cursor-pointer',
        'hover:border-transparent hover:bg-ds-brand-subtlest hover:text-ds-brand-default',
        'focus-visible:border-transparent focus-visible:bg-ds-brand-subtlest focus-visible:text-ds-brand-default',
        className
      )}
    >
      <ChatTeardropDotsIcon size={13} weight="fill" aria-hidden />
      <span className="min-w-0 truncate">{label ?? 'Ask Ellie'}</span>
    </button>
  );
}

interface AskEllieWrapperProps {
  children: React.ReactNode;
  widgetTitle: string;
  widgetContext: string;
  className?: string;
  isLoading?: boolean;
  disabled?: boolean;
}

/**
 * Attaches an "Ask Ellie" trigger to a dashboard widget, floated in the
 * content's top-right corner. Only correct when that corner is empty — a single
 * card, chart, or table with a free header.
 *
 * The trigger is always visible (see `AskEllieButton`). It never takes a row of
 * its own: a right-aligned strip above the content burns ~34px of vertical space
 * on every widget it touches, which stacked up to whole empty bands across the
 * dashboards. When the corner is NOT free — a grid of stat tiles, or a header
 * that already carries a filter or timestamp — put `AskEllieButton` in the
 * nearest existing header row instead (`InsightCard`'s `headerRight` slot, a
 * section heading's action slot, or the page header), so it costs no height.
 *
 * Args:
 *     children: The widget to attach the trigger to.
 *     widgetTitle: Widget name shown in the sidebar; also the accessible name.
 *     widgetContext: Serialized widget data sent as conversation context.
 *     className: Extra classes for the wrapper element (layout only).
 *     isLoading: Hides the trigger while the widget has no data to talk about.
 *     disabled: Hides the trigger (e.g. the widget is empty).
 */
export default function AskEllieWrapper({
  children,
  widgetTitle,
  widgetContext,
  className,
  isLoading,
  disabled,
}: AskEllieWrapperProps) {
  const showTrigger = !isLoading && !disabled;

  return (
    <div className={cn('relative', className)}>
      {children}
      {showTrigger && (
        <AskEllieButton
          widgetTitle={widgetTitle}
          widgetContext={widgetContext}
          className="absolute top-3 right-3 z-10"
        />
      )}
    </div>
  );
}
